// @ts-nocheck

import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  Snackbar,
  Alert,
  ThemeProvider,
  Fade,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import HeaderBar, { CurrentPages } from '../header-bar/HeaderBar';
import { useEffect, useState } from 'react';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';
import { useTranslation } from 'react-i18next';
import theme from '../resources/NodeTheme';
import EditableTable from '../components/EditableTable';
import PageHeader from '../components/PageHeader';
import GradientBackground from '../resources/components/GradientBackground';

const DescriptionPage = () => {
  const { t } = useTranslation();
  const [activeOrganisation] = useActiveOrganisation();

  const [descriptions, setDescriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Fetch metadata on component mount
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!activeOrganisation?.organizationId) {
        console.log('No active organization found');
        return;
      }

      try {
        console.log(
          'Fetching metadata for organization:',
          activeOrganisation.organizationId
        );
        const response = await fetch(
          `/api/db/business/metadata/business_description`,
          {
            headers: {
              'X-Organization': activeOrganisation.organizationId,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Received metadata:', data);

        if (data && data.value) {
          let descriptionsArray;
          try {
            // Try to parse the value as JSON first
            const parsedValue = JSON.parse(data.value);
            if (Array.isArray(parsedValue)) {
              descriptionsArray = parsedValue.map(description => {
                if (typeof description === 'object' && description.content && description.timestamp) {
                  return description;
                }
                return {
                  content: description,
                  timestamp: Date.now()
                };
              });
            } else {
              descriptionsArray = [{
                content: data.value,
                timestamp: Date.now()
              }];
            }
          } catch (e) {
            // If parsing fails, treat it as a string
            descriptionsArray = [{
              content: data.value,
              timestamp: Date.now()
            }];
          }
          setDescriptions(descriptionsArray.map(({ content, timestamp }) => ({ 
            value: content,
            timestamp
          })));
        } else {
          console.log('No value found in metadata');
          setDescriptions([]);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching metadata:', error);
        setIsLoading(false);
        setSnackbarMessage('Failed to load descriptions data: ' + error.message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    fetchMetadata();
  }, [activeOrganisation]);

  const handleSave = async () => {
    try {
      // Create an array of description strings
      const value = descriptions
        .map((description) => ({
          content: description.value.trim(),
          timestamp: description.timestamp || Date.now()
        }))
        .filter((description) => description.content !== '');

      const metadataItem = {
        id: 'business_description',
        key: 'business_description',
        value: JSON.stringify(value),
      };

      await fetch(`/api/db/business/metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization': activeOrganisation.organizationId,
        },
        body: JSON.stringify(metadataItem),
      });

      setSnackbarMessage(t('Descriptions saved successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error(t('Error saving description:'), error);
      setSnackbarMessage(`${t('Failed to save descriptions')}: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleAddRow = () => {
    setDescriptions([...descriptions, { value: '' }]);
  };

  return (
    <ThemeProvider theme={theme}>
      <GradientBackground sx={{ minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <HeaderBar
            currentPage={CurrentPages.Description}
            setCurrentPage={() => {}}
          />
          <Fade in={!isLoading} timeout={500}>
            <Container maxWidth="md" sx={{
              padding: { xs: 2, sm: 3 },
            }}>
              <PageHeader title={t('Description')} />
              
              <EditableTable
                title={t('Company Descriptions')}
                description={t('Manage your company descriptions below. Each row represents a separate description.')}
                instructions={descriptions}
                setInstructions={setDescriptions}
                isLoading={isLoading}
                columnHeader={t('Description')}
              />

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: { xs: 'stretch', sm: 'space-between' },
                }}
              >
                <Button 
                  variant="floating" 
                  startIcon={<AddIcon />} 
                  onClick={handleAddRow} 
                  disabled={isLoading}
                  sx={{ 
                    flex: { xs: 1, sm: 'none' },
                  }}
                >
                  {t('Add Row')}
                </Button>
                <Button 
                  variant="contained" 
                  startIcon={<SaveIcon />}
                  onClick={handleSave} 
                  disabled={isLoading}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  {t('Save Descriptions')}
                </Button>
              </Box>

              {/* Snackbar for notifications */}
              <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              >
                <Alert
                  onClose={handleCloseSnackbar}
                  severity={snackbarSeverity}
                  sx={{ width: '100%' }}
                >
                  {snackbarMessage}
                </Alert>
              </Snackbar>
            </Container>
          </Fade>
        </Box>
      </GradientBackground>
    </ThemeProvider>
  );
};

export default DescriptionPage;
